import React, { useCallback, useEffect, useState } from "react";
import { ArrowRight, CheckCircle2, RefreshCw, ShieldAlert } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { KD9LogoCircle } from "@/components/KD9Logo";

const PRIORITY_LABEL = {
  critical: "Acción importante",
  high: "Actualización prioritaria",
  normal: "Actualización KD9",
  low: "Información",
};

const UserEventGate = ({ children }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [validated, setValidated] = useState(false);
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setValidated(false);
    setError("");
    try {
      const { data } = await api.get("/api/user-event-messages/pending");
      setMessages(Array.isArray(data) ? data : []);
      setValidated(true);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const current = messages[0];

  const confirm = async () => {
    if (!current || busy) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/api/user-event-messages/${current.id}/seen`);
      const target = current.action?.path;
      const remaining = messages.slice(1);
      setMessages(remaining);
      if (remaining.length === 0 && target) {
        navigate(target, { replace: true });
      }
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5" style={{ background: "var(--kd9-bg)" }} data-testid="user-event-gate-loading">
        <div className="text-center">
          <KD9LogoCircle size={72} />
          <div className="mt-5 font-cinzel tracking-[0.28em] text-xs kd9-gold-text">KD9 · VALIDANDO ACCESO</div>
        </div>
      </div>
    );
  }

  if (!validated) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "var(--kd9-bg)" }} data-testid="user-event-gate-blocked">
        <section className="kd9-card w-full max-w-xl p-8 text-center">
          <div className="flex justify-center"><ShieldAlert size={34} style={{ color: "var(--kd9-gold)" }} /></div>
          <h1 className="mt-5 font-cinzel text-xl tracking-[0.08em]">NO PUDIMOS VALIDAR TUS MENSAJES</h1>
          <p className="mt-4 font-cormorant text-lg" style={{ color: "var(--kd9-text-soft)" }}>
            Por seguridad, el panel permanecerá bloqueado hasta completar esta validación.
          </p>
          {error && <p className="mt-4 text-sm" style={{ color: "#FCA5A5" }}>{error}</p>}
          <button type="button" onClick={load} className="kd9-btn-primary mt-6 rounded-xl px-5 py-3 inline-flex items-center gap-2">
            <RefreshCw size={16} /> Reintentar validación
          </button>
        </section>
      </div>
    );
  }

  if (!current) return children;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 relative overflow-hidden" style={{ background: "var(--kd9-bg)" }} data-testid="user-event-gate">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 20%, rgba(212,175,55,0.12), transparent 42%)" }} />
      <section className="kd9-card relative w-full max-w-2xl p-6 sm:p-10 text-center" style={{ borderColor: current.priority === "critical" ? "rgba(212,175,55,0.65)" : "var(--kd9-border)" }}>
        <div className="flex justify-center"><KD9LogoCircle size={82} /></div>
        <div className="mt-5 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 font-mont text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--kd9-gold)", borderColor: "rgba(212,175,55,0.35)", background: "rgba(212,175,55,0.08)" }}>
          <ShieldAlert size={13} />
          {PRIORITY_LABEL[current.priority] || "Actualización KD9"}
        </div>

        <h1 className="mt-6 font-cinzel text-2xl sm:text-3xl tracking-[0.08em]" style={{ color: "var(--kd9-text)" }}>
          {current.subject || "TIENES UN MENSAJE ANTES DE CONTINUAR"}
        </h1>
        <p className="mt-5 font-cormorant text-xl sm:text-2xl leading-relaxed" style={{ color: "var(--kd9-text)" }} data-testid="user-event-message-text">
          {current.text}
        </p>

        {messages.length > 1 && (
          <p className="mt-4 font-mont text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--kd9-text-soft)" }}>
            Mensaje 1 de {messages.length} · se mostrarán por prioridad
          </p>
        )}

        {error && (
          <div className="mt-5 rounded-lg border px-4 py-3 text-sm" style={{ borderColor: "rgba(239,68,68,.4)", color: "#FCA5A5", background: "rgba(127,29,29,.12)" }} data-testid="user-event-gate-error">
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={confirm}
          disabled={busy}
          className="kd9-btn-primary mt-7 w-full min-h-12 rounded-xl px-5 flex items-center justify-center gap-2 font-mont text-xs uppercase tracking-[0.16em] disabled:opacity-50"
          data-testid="user-event-confirm"
        >
          <CheckCircle2 size={17} />
          {busy ? "Confirmando..." : (current.action?.label || "Confirmar y continuar")}
          {!busy && <ArrowRight size={16} />}
        </button>

        <p className="mt-4 font-mont text-[9px] uppercase tracking-[0.14em]" style={{ color: "var(--kd9-text-soft)" }}>
          Al confirmar, este mensaje se marca como leído y se habilita tu acceso al panel.
        </p>
      </section>
    </div>
  );
};

export default UserEventGate;
